import {
  Button,
  Checkbox,
  LoadingOverlay,
  ModalProps,
  Stack,
  TextInput,
} from "@mantine/core"
import { DefaultModal } from "../../../../-components/default-modal"
import { useForm } from "@mantine/form"
import { PartialPlanLayoutAttachment } from "../../../../@client/Theasoft_ApiServer_Attachments"
import { useFileDialog } from "@mantine/hooks"
import { MIME_TYPES } from "@mantine/dropzone"
import { usePlanLayoutAttachmentApi } from "../../../../attachment-api/planLayoutAttachmentApi"
import { useTranslation } from "react-i18next"
import { FormButtonGroup } from "../../../../-components/form-button-group"
import { CancelButton, SubmitButton } from "../../../../-components/Buttons"
import { showErrorNotification } from "../../../../utils/form-submission-notifications"
import { useInvalidate } from "../../../../typed-client/hooks/use-invalidate"
import { DateOnly } from "../../../../@client/System"

interface Props extends ModalProps {
  planLayoutId: number
  startDate: DateOnly
  endDate: DateOnly
}

export function PlanLayoutAttachmentUploadModal({
  planLayoutId,
  startDate,
  endDate,
  ...props
}: Props) {
  const form = useForm<
    Omit<
      PartialPlanLayoutAttachment,
      "planLayoutId" | "startDate" | "endDate"
    > & {
      file: File | null
    }
  >({
    mode: "uncontrolled",
    initialValues: {
      attachment: {
        attachmentName: "",
        ownerGroupId: null,
        note: "",
        sort: 0,
      },
      description: "",
      file: null,
      publishOnDashboard: true,
      showWithoutPermission: false,
    },
    validate: {
      file: (value) => (value ? null : "File is required"),
    },
  })

  const fileDialog = useFileDialog({
    accept: MIME_TYPES.pdf,
    multiple: false,
    onChange: (files) => handleChangeFile(files?.[0] || null),
  })

  const handleChangeFile = (file: File | null) => {
    form.setFieldValue("file", file)
    form.setFieldValue("attachment.attachmentName", file?.name || "")
  }

  const planLayoutAttachmenApi = usePlanLayoutAttachmentApi()

  const invalidate = useInvalidate()

  const [loading, setLoading] = React.useState(false)

  const handleSubmit = async (values: typeof form.values) => {
    if (!values.file) {
      return
    }
    setLoading(true)
    await planLayoutAttachmenApi
      .addAttachmentAndMetadata(
        values.file,
        { ...values, planLayoutId, startDate, endDate },
        planLayoutId
      )
      .catch((err) => showErrorNotification(err))
      .then(() => {
        invalidate("/api/planLayout/attachments/GetPlanLayoutAttachments")
        handleClose()
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleClose = () => {
    if (loading) {
      return
    }
    handleChangeFile(null)
    form.reset()
    props.onClose?.()
  }

  const { t } = useTranslation("app_server")

  return (
    <DefaultModal title={t("UploadPlan")} {...props}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Button variant="outline" onClick={() => fileDialog.open()}>
            {form.getValues().file
              ? form.getValues().file?.name
              : t("SelectFile")}
          </Button>
          <TextInput
            label={t("Description")}
            key={form.key("description")}
            {...form.getInputProps("description")}
          />
          <Checkbox
            label={t("PublishOnDashboard")}
            key={form.key("publishOnDashboard")}
            {...form.getInputProps("publishOnDashboard", {
              type: "checkbox",
            })}
          />
          <Checkbox
            label={t("ShowWithoutPermission")}
            key={form.key("showWithoutPermission")}
            {...form.getInputProps("showWithoutPermission", {
              type: "checkbox",
            })}
          />
          <FormButtonGroup>
            <CancelButton onClick={handleClose} />
            <SubmitButton>{t("Upload")}</SubmitButton>
          </FormButtonGroup>
        </Stack>
      </form>
      <LoadingOverlay visible={loading} />
    </DefaultModal>
  )
}
